import createReducer from 'redux/lib/reducerConfig';
import {
  selectDataCutDisplayModeConfig,
  selectMassPriceJobActiveDataPointConfig,
  selectUserMassPriceSurveyConfig
} from 'redux/user/selectors';
import { reportError } from 'utils/reporting';
import { updateUserSettings } from 'redux/user';
import { showNotification } from 'common/function';
import { CompositeService, JobMappingService, PricejobService } from 'types';
import { runCancelableQuery } from 'utils/runCancelableQuery';
import { selectFilter, selectMarketPriceCutsData, selectSurveyDataCutPaymarketMappingsMap } from '../selectors';
import { getCompositesFromNotifications } from '../utils';
import { JobMode } from '../constants';
import { setFilter } from '../filter';

export const ActionTypes = {
  SET_DATA: 'MARKET_PRICE/CUTS/SET_DATA',
  UPDATE_COMPOSITE_DATA: 'MARKET_PRICE/CUTS/UPDATE_COMPOSITE_DATA',
  MASS_COMPOSITE_UPDATE_BY_JOB_SUCCESS: 'MARKET_PRICE/CUTS/MASS_COMPOSITE_UPDATE_BY_JOB_SUCCESS',
  SET_JOB_MODE: 'MARKET_PRICE/CUTS/SET_JOB_MODE',
  SET_LOADING: 'MARKET_PRICE/CUTS/SET_LOADING',
  SET_FILTER: 'MARKET_PRICE/CUTS/SET_FILTER',
  SET_GROUP_BY_VALUE: 'MARKET_PRICE/SET_GROUP_BY_VALUE',
  RELOAD_COMPOSITES: 'MARKET_PRICE/CUTS/RELOAD_COMPOSITES',
};

const setDataLoading = (loading) => ({ type: ActionTypes.SET_LOADING, payload: loading });

const setCutsData = ({
 data, surveyDataCutPaymarketMappings, surveyData,
}) => ({ type: ActionTypes.SET_DATA, payload: { data, surveyDataCutPaymarketMappings, surveyData } });

const reloadComposites = (data) => ({ type: ActionTypes.RELOAD_COMPOSITES, payload: { data } });

export const updateCompositeData = (composite) => ({
  type: ActionTypes.UPDATE_COMPOSITE_DATA,
  payload: composite,
});
export const massCompositeUpdateByJobSuccess = (payload) => ({
  type: ActionTypes.MASS_COMPOSITE_UPDATE_BY_JOB_SUCCESS,
  payload,
});

export const fetchMassMarketPriceData = ({ filter, pagination, sort, jobMode, activeDataPoint }) => async (dispatch) => {
  try {
    const params = { ...filter, ...pagination, sort, jobMode, activeDataPoint };

    dispatch(setDataLoading(true));

    const response = await runCancelableQuery('fetchMassMarketPriceData', () => PricejobService.getMassPricejobData({
      requestBody: params,
    }));

    if (response) {
      const { rows: data, surveyDataCutPaymarketMappings, surveyData } = response;

      dispatch(setCutsData({
        data,
        surveyDataCutPaymarketMappings,
        surveyData,
      }));
    }
  } catch (e) {
    reportError(e);
  }

  dispatch(setDataLoading(false));
};

export const reloadMassMarketPriceData = (notifications) => async (dispatch, getState) => {
  try {
    const state = getState();
    const filter = selectFilter(state);
    const currentComposites = selectMarketPriceCutsData(state);
    const activeDataPoint = selectMassPriceJobActiveDataPointConfig(state);
    const dataCutDisplayMode = selectDataCutDisplayModeConfig(state);
    const compositesToReload = getCompositesFromNotifications(currentComposites, notifications);

    if (compositesToReload.length) {
      const params = {
        compositeIds: compositesToReload,
        activeDataPoint,
        dataCutDisplayMode,
        plan: filter.plan,
        payMarket: filter.payMarket
      };
      const { rows, surveyData } = await PricejobService.getMassPricejobData({
        requestBody: params,
      });

      dispatch(reloadComposites({ rows, surveyData }));
    }
  } catch (e) {
    reportError(e);
  }
};

export const updateSurveyOrder = (oldId, newId, paymarket) => async (dispatch, getState) => {
  try {
    const userMassPriceSurveyConfig = selectUserMassPriceSurveyConfig(getState());
    const surveys = Object.keys(selectSurveyDataCutPaymarketMappingsMap(getState()));
    const paymarketConfig = (userMassPriceSurveyConfig[paymarket] && userMassPriceSurveyConfig[paymarket].length)
      ? [...userMassPriceSurveyConfig[paymarket]]
      : surveys;

    surveys.forEach((id) => {
      if (!paymarketConfig.includes(id)) {
        paymarketConfig.push(id);
      }
    });

    const newUserSettings = paymarketConfig.map((surveyId) => {
      if (surveyId === oldId) {
        return newId;
      }

      if (surveyId === newId) {
        return oldId;
      }

      return surveyId;
    });

    dispatch(updateUserSettings({
      massPriceSurveyConfig: {
        ...userMassPriceSurveyConfig,
        [paymarket]: newUserSettings,
      },
    }));
  } catch (e) {
    reportError(e);
  }
};

export const setJobMode = (mode) => (dispatch, getState) => {
  const filter = selectFilter(getState());
  dispatch({ type: ActionTypes.SET_JOB_MODE, payload: mode });
  if (mode !== JobMode.JOBS) {
    dispatch(setFilter({ ...filter, plan: filter.plan.slice(0, 1) }));
  }
};

export const addMassSurveyDataAction = ({ dataCutId, paymarketId, surveyId, filter }) => async (dispatch) => {
  try {
    dispatch(setDataLoading(true));
    await CompositeService.addMassCompositeDataCut({
      requestBody: {
        dataCutId,
        paymarketId,
        surveyId,
        ...filter,
      },
    });
  } catch (err) {
    reportError(err);
    showNotification('Failed to add Survey Data', 'error');
  }

  dispatch(setDataLoading(false));
};

export const removeMassSurveyDataAction =
  ({ dataCutId, paymarketId, surveyId, filter }) =>
  async (dispatch) => {
    try {
      dispatch(setDataLoading(true));
      await CompositeService.deleteMassCompositeDataCut({
        requestBody: {
          dataCutId,
          paymarketId,
          surveyId,
          ...filter,
        },
      });
    } catch (err) {
      reportError(err);
      showNotification('Failed to remove Survey Data', 'error');
    }

  dispatch(setDataLoading(false));
};

export const deleteJobMapping = ({ compositeId, mapping, activeDataPoint, dataCutDisplayMode }) => async (dispatch) => {
  try {
    await JobMappingService.unmapCompanyJobToSurveyJobs({
      requestBody: {
        mappingIds: [mapping.id]
      }
    });

    const { rows: [composite] } = await PricejobService.getCompositeMassPricejobData({
      compositeId,
      activeDataPoint,
      dataCutDisplayMode,
    });

    dispatch(updateCompositeData(composite));
  } catch (err) {
    reportError(err);
    showNotification('Failed delete job mapping', 'error');
  }
};

export const undoJobMappingDeletion = ({ compositeId, mapping, compositeDataCuts, activeDataPoint, dataCutDisplayMode }) => async (dispatch) => {
  try {
    await JobMappingService.mapCompanyJobToSurveyJobs({
      requestBody: {
        companyJobId: mapping.companyJobId,
        planId: mapping.planId,
        mappings: [{
          surveyJobDefinitionId: mapping.surveyJobDefinitionId,
          paymarketId: mapping.paymarketId
        }]
      }
    });

    if (compositeDataCuts.length) {
      await CompositeService.addCompositeDataCuts({
        compositeId,
        requestBody: {
          surveyData: compositeDataCuts.map(item => ({
            surveyDataId: item.surveyDataId,
            surveyId: item.surveyId,
            weight: item.weight,
            adjustment: item.adjustment
          })),
        },
      });
    }

    const { rows: [composite] } = await PricejobService.getCompositeMassPricejobData({
      compositeId,
      activeDataPoint,
      dataCutDisplayMode,
    });
    dispatch(updateCompositeData(composite));
  } catch (err) {
    reportError(err);
    showNotification('Failed undo delete job mapping', 'error');
  }
};

export const removeDataCutsFromJobFamily = (params) => async (dispatch) => {
  try {
    await CompositeService.deleteMassCompositeDataCut({
      requestBody: params,
    });
    dispatch(massCompositeUpdateByJobSuccess({ ...params, isRemoved: true }));
  } catch (err) {
    reportError(err);
    showNotification('Failed remove data cuts', 'error');
  }
};

export const applyDataCutsToJobFamily = (params) => async (dispatch) => {
  try {
    await CompositeService.addMassCompositeDataCut({
      requestBody: params,
    });
    dispatch(massCompositeUpdateByJobSuccess(params));
  } catch (err) {
    reportError(err);
    showNotification('Failed apply data cuts', 'error');
  }
};

export const addSurveyDataAction = ({ compositeId, surveyDataId, surveyId, activeDataPoint, dataCutDisplayMode }) => async (dispatch) => {
  try {
    await CompositeService.addCompositeDataCut({
      compositeId,
      surveyDataId,
      requestBody: {
        surveyId,
      },
    });

    const { rows: [composite] } = await PricejobService.getCompositeMassPricejobData({
      compositeId,
      activeDataPoint,
      dataCutDisplayMode,
    });

    dispatch(updateCompositeData(composite));
  } catch (err) {
    reportError(err);
    showNotification('Failed to add Survey Data', 'error');
  }
};

export const removeSurveyDataAction = ({ compositeId, surveyDataId, activeDataPoint, dataCutDisplayMode }) => async (dispatch) => {
  try {
    await CompositeService.deleteCompositeDataCut({
      compositeId,
      surveyDataId,
    });
    const { rows: [composite] } = await PricejobService.getCompositeMassPricejobData({
      compositeId,
      activeDataPoint,
      dataCutDisplayMode,
    });

    dispatch(updateCompositeData(composite));
  } catch (err) {
    reportError(err);
    showNotification('Failed to remove Survey Data', 'error');
  }
};

const cutsInitialState = {
  data: [],
  surveyDataCutPaymarketMappings: [],
  surveyData: [],
  loading: false,
  jobMode: JobMode.JOBS,
};

export const cutsReducer = createReducer(
  cutsInitialState,
  {
    [ActionTypes.SET_DATA]: (state, { payload: { data, surveyDataCutPaymarketMappings, surveyData } }) => {
      return {
        ...state,
        data: data,
        surveyDataCutPaymarketMappings: surveyDataCutPaymarketMappings,
        surveyData: surveyData,
      };
    },
    [ActionTypes.RELOAD_COMPOSITES]: (state, { payload: { data } }) => {
      const { rows, surveyData } = data;
      const newSurveyData = surveyData.filter(
        item => !state.surveyData.find(({ id }) => id === item.id)
      );

      return {
        ...state,
        data: state.data.map((composite) => {
          const newComposite = rows.find(({ id }) => id === composite.id);
          return newComposite || composite;
        }),
        surveyData: [...state.surveyData, ...newSurveyData]
      };
    },
    [ActionTypes.UPDATE_COMPOSITE_DATA]: (state, { payload: newComposite }) => ({
      ...state,
      data: state.data.map(composite => {
        if (newComposite && composite.id === newComposite.id) {
          return newComposite;
        }

        return composite;
      }),
    }),
    [ActionTypes.SET_JOB_MODE]: (state, { payload }) => {
      return {
        ...state,
        data: [],
        count: 0,
        jobMode: payload,
      };
    },
    [ActionTypes.SET_FILTER]: (state) => ({
      ...state,
      surveys: [],
      data: [],
      count: 0,
    }),
    [ActionTypes.SET_GROUP_BY_VALUE]: (state) => ({
      ...state,
      surveys: [],
      data: [],
      count: 0,
    }),
    [ActionTypes.SET_LOADING]: (state, { payload }) => {
      return {
        ...state,
        loading: payload,
      };
    },
    [ActionTypes.MASS_COMPOSITE_UPDATE_BY_JOB_SUCCESS]: (state, { payload }) => ({
      ...state,
      data: state.data.map(data => {
        if (payload.jobSubFamilyId?.includes(data.id) || payload.jobFamilyId?.includes(data.id)) {
          const key = `${payload.surveyId}-${payload.dataCutId}`;
          return {
            ...data,
            stats: {
              ...data.stats,
              [key]: {
                ...data.stats[key],
                checkedJobs: payload.isRemoved ? 0 : data.stats[key].allJobs,
              },
            },
          };
        }

        return data;
      }),
    }),
  }
);
